/**
 * Verlof van mijn team (Story 8.4, besluit V39) — de LEESWEG.
 *
 * WAAROM DEZE PAGINA BESTAAT, en waarom hij níet de goedkeuringslijst is.
 * De vastgestelde zinnen van §10.6 leven in het verloop van een aanvraag: de
 * medewerker leest zijn afwijzing met reden, de leidinggevende die eerder
 * goedkeurde leest zijn eigen goedkeuring én de overruling, en bij
 * calamiteiten- of zorgverlof leest hij alleen dát de afwezigheid vervalt —
 * zonder reden. Tot deze story opende geen enkel scherm dat verloop voor een
 * leidinggevende: de zinnen waren geschreven en onleesbaar.
 *
 * De goedkeuringslijst kon die weg niet zijn. Daar staat wat hij moet
 * BESLISSEN, en een aanvraag waarover HR als enige beslist hoort daar in geen
 * enkele stand in te staan (FR-24, besluit V27). Deze pagina staat ernaast:
 * hier staat wat hij mag LEZEN — en juist dáárom staat een afgewezen
 * calamiteitenverlof hier wél (code review 2026-08-17). Zonder die rij mist
 * deze pagina precies de aanvraag waarvoor besluit V39 haar heeft laten
 * bouwen. Wat een regel prijsgeeft is een naam, een periode en een stand; de
 * inhoud blijft achter het verloop, dat zijn eigen, striktere filter draagt.
 *
 * ER STAAT GEEN REDEN OP DEZE LIJST (§8.3). De regelvorm van de server draagt
 * er geen; de reden die hij wél hoort te lezen komt uit het verloop, dat zijn
 * eigen filter draagt (story 7.4/8.4).
 *
 * Toegankelijkheid (KE-3): het verloop hangt aan een disclosure-knop die zegt
 * over wíens verlof het gaat — één pagina met vijf identieke knoppen *Verloop*
 * is met een schermlezer niet te bedienen (UX-DR15).
 */

import { Fragment, useState } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth';
import { useTeamVerlof } from './hooks/useTeamVerlof';
import { LeaveHistoryList } from './LeaveHistoryList';
import { StatusBadge } from './StatusBadge';
import { formatNlUren } from './urenFormat';

/** UX-DR14: "6 aug" — dag + korte maandnaam, geen jaartal. */
function formatKorteDatum(iso: string): string {
  const [jaar, maand, dag] = iso.split('-').map(Number);
  const datum = new Date(Date.UTC(jaar ?? 1970, (maand ?? 1) - 1, dag ?? 1));
  return datum.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function periodeTekst(startDate: string, endDate: string): string {
  return startDate === endDate
    ? formatKorteDatum(startDate)
    : `${formatKorteDatum(startDate)} — ${formatKorteDatum(endDate)}`;
}

/**
 * De kop en de inleiding hangen aan de ROL, want de lijst doet dat ook (code
 * review 2026-08-17). HR en beheerders krijgen van de server élke aanvraag van
 * de organisatie; *"Verlof van mijn team"* is voor hen onwaar, en een pagina die
 * iets anders belooft dan ze toont wordt verkeerd gelezen.
 */
const KOP_LEIDINGGEVENDE = 'Verlof van mijn team';
const KOP_HR = 'Verlof van de organisatie';

const INLEIDING_LEIDINGGEVENDE =
  'Hier staat het verlof van je teamleden. Er valt hier niets te beslissen — dat doe je bij Goedkeuringen. Open het verloop om te zien wat er met een aanvraag gebeurd is.';
const INLEIDING_HR =
  'Hier staat het verlof van alle medewerkers. Er valt hier niets te beslissen — dat doe je bij Goedkeuringen. Open het verloop om te zien wat er met een aanvraag gebeurd is.';

/**
 * De begrenzing die de server aanhoudt, in dezelfde woorden op het scherm: een
 * lijst die stilzwijgend ophoudt leest als een lijst die compleet is.
 */
const PERIODE_REGEL =
  'Je ziet verlof dat in de afgelopen twaalf maanden afliep, plus alles wat nog komt.';

/** De lege lijst en een storing zijn twee verschillende mededelingen. */
const NIETS_TE_TONEN_LEIDINGGEVENDE = 'Er staat op dit moment geen verlof van je team.';
const NIETS_TE_TONEN_HR = 'Er staat op dit moment geen verlof.';
const OPHALEN_MISLUKT =
  'Het verlof kon niet opgehaald worden. Probeer het over een moment opnieuw.';

export function TeamVerlofPage() {
  const { data, isLoading, isError } = useTeamVerlof();
  const { hasRole } = useAuth();
  const [openId, setOpenId] = useState<number | null>(null);

  const regels = data?.data ?? [];
  const zietDeHeleOrganisatie = hasRole(['hr', 'admin']);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <h1 className="text-xl font-bold text-[#1E293B]">
          {zietDeHeleOrganisatie ? KOP_HR : KOP_LEIDINGGEVENDE}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {zietDeHeleOrganisatie ? INLEIDING_HR : INLEIDING_LEIDINGGEVENDE}
        </p>
        <p className="mt-1 text-sm text-gray-500">{PERIODE_REGEL}</p>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Bezig met laden…</p>}

        {/* EEN STORING IS GEEN LEGE LIJST (code review 2026-08-17). De oude
            tekst meldde bij een mislukte aanroep dat er geen verlof was — een
            onware mededeling, en precies degene die iemand laat stoppen met
            zoeken. Wie deze lijst niet mag zien krijgt een 403, en die leest
            hier als een storing; dat is een eerlijker uitkomst dan een lege
            pagina die suggereert dat er niets is. */}
        {!isLoading && isError && (
          <p role="alert" className="mt-6 text-sm text-[#D64545]">
            {OPHALEN_MISLUKT}
          </p>
        )}

        {!isLoading && !isError && regels.length === 0 && (
          <p className="mt-6 text-sm text-gray-500">
            {zietDeHeleOrganisatie ? NIETS_TE_TONEN_HR : NIETS_TE_TONEN_LEIDINGGEVENDE}
          </p>
        )}

        {!isLoading && !isError && regels.length > 0 && (
          <table className="mt-6 w-full text-sm" data-testid="team-verlof-tabel">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2">Medewerker</th>
                <th className="px-4 py-2">Periode</th>
                <th className="px-4 py-2">Uren</th>
                <th className="px-4 py-2">Stand</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {regels.map((regel) => {
                const isOpen = openId === regel.id;
                return (
                  <Fragment key={regel.id}>
                    <tr className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-2 text-[#1E293B]">{regel.employeeNaam}</td>
                      <td className="px-4 py-2 text-[#1E293B]">
                        {periodeTekst(regel.startDate, regel.endDate)}
                        {regel.startTime !== null && regel.endTime !== null && (
                          <span className="text-gray-500">
                            {' '}
                            {regel.startTime}–{regel.endTime}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-[#1E293B]">{formatNlUren(regel.hours)}</td>
                      <td className="px-4 py-2">
                        <StatusBadge
                          status={regel.requestStatus}
                          hours={regel.hours}
                          displayStatus={null}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={`team-verlof-verloop-${regel.id}`}
                          onClick={() => setOpenId(isOpen ? null : regel.id)}
                          className="text-xs text-[#54949E] hover:underline"
                        >
                          Verloop
                          <span className="sr-only"> — {regel.employeeNaam}</span>
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-[#E2E8F0]">
                        <td
                          colSpan={5}
                          className="px-4 py-2"
                          id={`team-verlof-verloop-${regel.id}`}
                        >
                          <LeaveHistoryList
                            requestId={regel.id}
                            isOpen={isOpen}
                            naam={`Verloop van het verlof van ${regel.employeeNaam}`}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
